import { Injectable } from '@angular/core';
import {
  ColDef,
  CellClickedEvent,
  CheckboxSelectionCallbackParams,
  EditableCallbackParams,
  ICellRendererParams,
  ValueGetterParams,
  CellClassParams,
} from 'ag-grid-community';
import { Observable } from 'rxjs';

import { DateFormatService } from './date-format.service';
import { FormatService } from './format.service';
import { LanguageService } from './language.service';
import { MenuService } from './menu.service';
import { UtilityService } from './utility.service';
import { AggFunc, BooleanStrings } from '../constants';
import { GridFieldOptions, IdDescription } from '../models';
import {
  ButtonRendererComponent,
  CheckboxRendererComponent,
  DateFilterComponent,
  DateRendererComponent,
  ImageRendererComponent,
  SelectRendererComponent,
} from '../components';

@Injectable({
  providedIn: 'root',
})
export class GridService {
  components: any = {
    agDateInput: DateFilterComponent,
    buttonRenderer: ButtonRendererComponent,
    checkboxRenderer: CheckboxRendererComponent,
    dateRenderer: DateRendererComponent,
    imageRenderer: ImageRendererComponent,
    selectRenderer: SelectRendererComponent,
  };

  constructor(
    protected formatService: FormatService,
    protected languageService: LanguageService,
    protected menuService: MenuService,
    protected dateFormatService: DateFormatService,
    protected utilityService: UtilityService,
  ) {}

  button<T>(
    onClick: (model: T) => void,
    header: string,
    field: string,
    className: string,
    iconClass: string | ((data: T) => string),
    show?: (data: T) => boolean,
    options?: GridFieldOptions,
    buttonText?: string,
  ): ColDef {
    return {
      headerName: header,
      field,
      cellClass: 'd-flex align-items-center',
      cellRenderer: 'buttonRenderer',
      cellRendererParams: {
        className,
        iconClass,
        show,
        onClick,
        buttonText,
      },
      width: 40,
      filter: false,
      sortable: false,
      suppressMenu: true,
      onCellClicked: () => {},
      ...options,
    };
  }

  commentButton(onClick: (model: any) => void): ColDef {
    return this.button(onClick, '', 'comment', 'comment', (model: any) =>
      !model.commentsExist ? 'far fa-comment' : 'fa fa-comment',
    );
  }

  calculation(field: string, headerName: string, calculation: (model: any) => any, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      calculation,
      valueGetter: (params: ValueGetterParams) => (params.data[field] ? params.data[field] : calculation(params.data)),
      cellRenderer: GridService.aggregateCellRenderer,
    } as ColDef;
  }

  checkbox(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    const canEdit = (params) => editable && editable(params.data);
    return {
      ...this.text(field, headerName, options),
      cellClass: 'd-flex align-items-center',
      cellRenderer: 'checkboxRenderer',
      editable: (params: EditableCallbackParams) => canEdit(params),
      onCellValueChanged: (params) => (canEdit(params) && edited ? edited(params.data) : undefined),
      filterValueGetter: (params: ValueGetterParams) =>
        params.data[field] ? BooleanStrings.translatedYes : BooleanStrings.translatedNo,
    } as ColDef;
  }

  checkboxRowSelect(
    field: string,
    headerName: string,
    showSelectAll?: boolean | ((params: any) => boolean),
    showCheckbox?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef[] {
    return this.menuService.isReadOnly
      ? [this.checkbox('selected', ''), this.text(field, headerName)]
      : [
          {
            ...this.text(field, headerName, options),
            checkboxSelection: (params: CheckboxSelectionCallbackParams) =>
              showCheckbox ? showCheckbox(params.data) : true,
            headerCheckboxSelection: showSelectAll,
            headerCheckboxSelectionFilteredOnly: !!showSelectAll,
          } as ColDef,
        ];
  }

  clickableField(
    field: string,
    headerName: string,
    clicked?: (model: any) => void,
    clickable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    const canEdit = (params) => !params.data.isAggregate && (!clickable || clickable(params.data));
    return {
      ...this.text(field, headerName, options),
      cellRenderer: GridService.aggregateCellRenderer,
      onCellClicked: (params: CellClickedEvent) => (canEdit(params) && clicked ? clicked(params.data) : undefined),
      cellStyle: (params) =>
        canEdit(params)
          ? { color: 'blue', cursor: 'pointer', 'text-decoration': 'underline' }
          : { cursor: 'default', 'text-decoration': 'none' },
    };
  }

  date(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      comparator: this.dateComparator,
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: this.dateComparator,
        buttons: ['reset'],
      },
      valueGetter: (params: ValueGetterParams) => this.formatService.date(params.data ? params.data[field] : undefined),
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: params.data ? this.formatService.date(params.data[field]) : params.value,
        }),
      cellStyle: { overflow: 'visible' },
    };
  }

  dateTime(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.date(field, headerName, options),
      valueGetter: (params: ValueGetterParams) =>
        params.data ? this.formatService.dateTime(params.data[field]) : undefined,
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: params.data ? this.formatService.dateTime(params.data[field]) : params.value,
        }),
    };
  }

  editableDate(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
  ): ColDef {
    const canEdit = (params) => !params.data.isAggregate && (!editable || editable(params.data));
    if (this.menuService.isReadOnly) {
      return this.date(field, headerName, options);
    }

    return {
      ...this.date(field, headerName, options),
      cellClass: 'd-flex align-items-center',
      cellRenderer: 'dateRenderer',
      editable: (params: EditableCallbackParams) => canEdit(params),
      onCellValueChanged: (params) => (canEdit(params) && edited ? edited(params.data) : undefined),
    };
  }

  editableField(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
    cellStyle?: (params: CellClassParams) => any,
    regex?: RegExp,
  ): ColDef {
    if (this.menuService.isReadOnly) {
      return this.text(field, headerName, options);
    }

    const canEdit = (params) => !params.data.isAggregate && (!editable || editable(params.data));
    return {
      ...this.text(field, headerName, options),
      cellStyle: (params) => ({
        ...(canEdit(params) ? { color: 'blue', cursor: 'text' } : { cursor: 'default' }),
        ...(cellStyle ? cellStyle(params) : {}),
      }),
      editable: (params: EditableCallbackParams) => canEdit(params),
      onCellValueChanged: (params) => (canEdit(params) && edited ? edited(params.data) : undefined),
      singleClickEdit: true,
      valueSetter: regex
        ? (params) => {
            if (regex.test(params.newValue ?? '')) {
              params.data[field] = params.newValue;
              return true;
            } else {
              return false;
            }
          }
        : undefined,
    };
  }

  editableNumberFormat(
    field: string,
    headerName: string,
    edited?: (model: any) => void,
    editable?: (model: any) => boolean,
    options?: GridFieldOptions,
    colourNegatives?: boolean,
    decimalPlaces?: number,
  ): ColDef {
    if (this.menuService.isReadOnly) {
      return this.numberFormat(field, headerName, options, colourNegatives, decimalPlaces);
    }

    return {
      ...this.editableField(field, headerName, edited, editable, options),
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: this.formatService.number(params.data ? params.data[field] : params.value, decimalPlaces),
        }),
      filterValueGetter: (params: ValueGetterParams) =>
        params.data ? this.formatService.number(params.data[field], decimalPlaces) : undefined,
    };
  }

  image(
    field: string,
    headerName: string,
    width: number = 200,
    height: number = 150,
    options?: GridFieldOptions,
  ): ColDef {
    return {
      ...this.text(field, headerName, options),
      cellRenderer: 'imageRenderer',
      cellRendererParams: {
        width,
        height,
      },
      width,
      autoHeight: true,
      filter: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      ...options,
    };
  }

  number(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
      },
    };
  }

  currency(
    currencyField: string,
    field: string,
    headerName: string,
    options?: GridFieldOptions,
    colourNegatives?: boolean,
    decimalPlaces?: number,
  ): ColDef {
    return {
      ...this.text(field, headerName, options),
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
      },
      filterValueGetter: (params: ValueGetterParams) =>
        params.data ? this.formatService.number(params.data[field], decimalPlaces) : undefined,
      cellRenderer: (params: ICellRendererParams) =>
        GridService.aggregateCellRenderer({
          ...params,
          value: this.formatService.currency(
            params.data ? params.data[field] : params.value,
            params.data?.[currencyField] ?? '',
            decimalPlaces,
          ),
        }),
      cellStyle: (params: CellClassParams) => (colourNegatives && params.value < 0 ? { color: 'red' } : {}),
      ...{ currencyField },
    };
  }

  numberFormat(
    field: string,
    headerName: string,
    options?: GridFieldOptions,
    colourNegatives?: boolean,
    decimalPlaces?: number,
  ): ColDef {
    return {
      ...this.text(field, headerName, options),
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
      },
      filterValueGetter: (params: ValueGetterParams) =>
        params.data ? this.formatService.number(params.data[field], decimalPlaces) : undefined,
      cellRenderer: (params: ICellRendererParams) => {
        const value = params.data ? params.data[field] : params.value;
        return GridService.aggregateCellRenderer({
          ...params,
          value:
            options?.zeroOrNegativeMessageField && params.data[options?.zeroOrNegativeMessageField] && value <= 0
              ? params.data[options?.zeroOrNegativeMessageField] ?? $localize`Out of stock`
              : this.formatService.number(value, decimalPlaces),
        });
      },
      cellStyle: (params) => (colourNegatives && params.value < 0 ? { color: 'red' } : {}),
    };
  }

  rowNumber(options?: GridFieldOptions): ColDef {
    return {
      valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
      maxWidth: 40,
      ...options,
    } as ColDef;
  }

  select(
    field: string,
    headerName: string,
    selectOptions?: Observable<IdDescription[]> | IdDescription[],
    options?: GridFieldOptions,
  ): ColDef {
    if (this.menuService.isReadOnly) {
      return this.text(field, headerName, options);
    }

    return {
      ...this.text(field, headerName, options),
      cellRenderer: 'selectRenderer',
      cellRendererParams: {
        options: this.utilityService.mapSelectOptions(selectOptions),
      },
    };
  }

  text(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      field,
      headerName: this.languageService.translate(headerName, options?.abbreviatedHeaderName),
      width: 200,
      filterParams: {
        buttons: ['reset'],
      },
      cellRenderer: GridService.aggregateCellRenderer,
      ...options,
    } as ColDef;
  }

  textEllipsis(field: string, headerName: string, options?: GridFieldOptions): ColDef {
    return {
      ...this.text(field, headerName, options),
      cellStyle: { overflow: 'hidden', 'text-overflow': 'ellipsis', 'white-space': 'nowrap' },
    } as ColDef;
  }

  private dateComparator = (filterLocalDateAtMidnight, cellValue) => {
    const cellDate = this.toDate(cellValue);
    const filterDate = this.toDate(filterLocalDateAtMidnight);

    if (!cellDate || !filterDate) {
      return -1;
    }

    if (filterDate.getTime() === cellDate.getTime()) {
      return 0;
    }

    if (cellDate < filterDate) {
      return -1;
    }

    if (cellDate > filterDate) {
      return 1;
    }
  };

  private toDate(date: string | Date): Date {
    if (date == undefined) {
      return undefined;
    }

    if (date instanceof Date) {
      return date;
    }

    return this.dateFormatService.dateFromString(date);
  }

  private static aggregateCellRenderer(params: ICellRendererParams) {
    if (!params.value && params.value !== 0) {
      return '';
    }
    if (params.data?.isAggregate) {
      return params.data[params.colDef.field] ? `<strong>${params.value}</strong>` : '';
    }

    const aggFunc = params.colDef.aggFunc;
    if (params.node.group) {
      switch (aggFunc) {
        case AggFunc.avg:
        case AggFunc.sum:
        case AggFunc.sumInteger:
        case AggFunc.count:
          return `<strong>${params.value}</strong>`;
        default:
          return params.value;
      }
    }

    switch (aggFunc) {
      case AggFunc.first:
      case AggFunc.last:
      case AggFunc.min:
      case AggFunc.max:
        return '';
      default:
        return params.value;
    }
  }
}
